import { type HTMLAttributes, useState } from "react"

import { cn } from "@/lib/utils"

// ---------------------------------------------------------------------------
// InitialsAvatar — the token-fill circular-initials avatar. Extracted from the
// children GuardiansCard's alternating `AVATAR_FILL` rotation so the treatment
// is single-point. Solid token fills (primary / chart-2) rotate by `fill`
// index, or pin one with an explicit `tone`. Size is arbitrary (px).
// ---------------------------------------------------------------------------

export type InitialsAvatarTone = "primary" | "sky"

/** Solid token fills, rotated by index (matches the original two-tone list). */
const FILL_ROTATION = [
  "bg-primary text-primary-foreground",
  "bg-[hsl(var(--chart-2))] text-[hsl(var(--chart-2-foreground))]",
] as const

const TONE_FILL: Record<InitialsAvatarTone, string> = {
  primary: FILL_ROTATION[0],
  sky: FILL_ROTATION[1],
}

/** First letter of the first + last whitespace-separated token, uppercased. */
function deriveInitials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean)
  if (parts.length === 0) return "?"
  const first = parts[0][0] ?? ""
  const last = parts.length > 1 ? parts[parts.length - 1][0] ?? "" : ""
  return (first + last).toUpperCase() || "?"
}

export interface InitialsAvatarProps extends Omit<HTMLAttributes<HTMLSpanElement>, "children"> {
  /** Full name to derive initials from when `initials` is not supplied. */
  name?: string
  /** Explicit initials — takes precedence over `name` (already-derived, exact). */
  initials?: string
  /** Rotation index into the token-fill list (ignored when `tone` is set). */
  fill?: number
  /** Pin an explicit fill tone instead of rotating by index. */
  tone?: InitialsAvatarTone
  /** Diameter in px (default 40). Controls circle size and font size. */
  size?: number
  /**
   * Optional signed photo URL. Additive — renders over the initials fill
   * when present, falls back to initials on absence or `onError` (e.g. an
   * expired signed URL). Existing call sites are unaffected.
   */
  imageUrl?: string | null
}

function InitialsAvatar({
  name,
  initials,
  fill = 0,
  tone,
  size = 40,
  className,
  style,
  imageUrl,
  ...props
}: InitialsAvatarProps) {
  const [imageFailed, setImageFailed] = useState(false)
  const label = initials?.trim() || (name ? deriveInitials(name) : "?")
  const fillClass = tone ? TONE_FILL[tone] : FILL_ROTATION[fill % FILL_ROTATION.length]
  const showImage = !!imageUrl && !imageFailed
  return (
    <span
      className={cn(
        "grid shrink-0 place-items-center overflow-hidden rounded-full font-bold",
        fillClass,
        className,
      )}
      style={{ width: size, height: size, fontSize: Math.round(size * 0.35), ...style }}
      {...props}
    >
      {showImage ? (
        <img
          src={imageUrl}
          alt=""
          className="h-full w-full object-cover"
          onError={() => setImageFailed(true)}
        />
      ) : (
        label
      )}
    </span>
  )
}

export { InitialsAvatar }
